<template>
  <Modal
    v-model="openFacebookAuthModal"
    :title="$t('settings.linked_networks.facebook.auth_modal.title')"
    :canClose="true"
    :closeOnEsc="false"
    :closeOnBackdropClick="false">
    <template #body>
      <div v-if="facebookAuthState === 'processing'" class="text-center">
        <h3 class="mb-3">{{ $t('settings.linked_networks.facebook.auth_modal.processing') }}</h3>
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">{{ $t('common.loading') }}</span>
        </div>
      </div>
      <div v-else-if="facebookAuthState === 'success'">
        <h4 class="mb-2">{{ facebookAuthResponse.message }}</h4>
        <div v-if="facebookAuthResponse.pages.length">
          <ul>
            <li v-for="page in facebookAuthResponse.pages" :key="page.id" class="mt-3">
              <strong>{{ page.name }}</strong>
              <span v-if="page.message || page.error" class="text-warning">- {{ page.message || page.error }}</span>
            </li>
          </ul>
        </div>
        <h5 class="mt-4">
          <strong>{{ $t('settings.linked_networks.facebook.note_title') }}:</strong>
          {{ $t('settings.linked_networks.facebook.note') }}
          <a class="text-primary" href="https://business.facebook.com" target="_blank" rel="noopener noreferrer">{{
            $t('settings.linked_networks.facebook.business_settings')
          }}</a>
        </h5>
      </div>
      <div v-else-if="facebookAuthState === 'error'" class="text-center">
        <IconExclamationCircle size="40" class="text-danger mb-2" />
        <p class="text-danger">{{ $t('settings.linked_networks.facebook.auth_modal.error') }}</p>
      </div>
    </template>
  </Modal>
</template>
<script setup lang="ts">
import { authorizeFacebookPageWithCode } from '@/api/network';
import translator from '@/locale/translator';
import { Modal } from '@prospective/lithium';
import { IconExclamationCircle } from '@tabler/icons-vue';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

interface FacebookAuthResponse {
  message: string;
  pages: Array<{
    id: string;
    mode: string;
    name: string;
    status: string;
    error?: string;
    message?: string;
  }>;
}

const route = useRoute();
const router = useRouter();
const openFacebookAuthModal = ref(false);
const facebookAuthState = ref('processing'); // 'processing' | 'success' | 'error'
const facebookAuthResponse = ref<FacebookAuthResponse>({ message: translator.get('app.something_wrong'), pages: [] });

onMounted(async () => {
  const code = route.query?.code;
  if (code) {
    authorizeFacebookPage(code as string);
    removeCodeFromUrlQuery();
  }
});

async function authorizeFacebookPage(code: string) {
  try {
    openFacebookAuthModal.value = true;
    facebookAuthResponse.value = (await authorizeFacebookPageWithCode({ code })) as any as FacebookAuthResponse;
    facebookAuthState.value = 'success';
  } catch (error) {
    console.error('Error authorizing Facebook page:', error);
    facebookAuthState.value = 'error';
    facebookAuthResponse.value = { message: translator.get('app.something_wrong'), pages: [] };
  }
}

function removeCodeFromUrlQuery() {
  const query = { ...route.query };
  delete query.code;
  router.replace({ query });
}
</script>
